/**
 * Alignment Map Filters Component
 * Left sidebar with filters for OKR alignment map
 */

import React from 'react';
import { X, Filter, Search, Calendar } from 'lucide-react';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';

// Helper functions for date calculations
const getQuarterDates = (year: number, quarter: number): { start: Date; end: Date } => {
  const quarterStartMonth = (quarter - 1) * 3;
  const start = new Date(year, quarterStartMonth, 1);
  const end = new Date(year, quarterStartMonth + 3, 0, 23, 59, 59);
  return { start, end };
};

const getCurrentQuarter = (): { year: number; quarter: number } => {
  const now = new Date();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  return { year: now.getFullYear(), quarter };
};

const getNextQuarter = (): { year: number; quarter: number } => {
  const { year, quarter } = getCurrentQuarter();
  if (quarter === 4) {
    return { year: year + 1, quarter: 1 };
  }
  return { year, quarter: quarter + 1 };
};

const getYearDates = (year: number): { start: Date; end: Date } => {
  return {
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31, 23, 59, 59),
  };
};

export interface AlignmentFilters {
  types: Set<string>;
  statuses: Set<string>;
  search: string;
  progressMin: number;
  progressMax: number;
  departments: Set<string>;
  timePeriod: {
    startDate: Date | null;
    endDate: Date | null;
    preset: string; // 'all' | 'current-quarter' | 'next-quarter' | 'current-year' | 'custom'
  };
}

interface AlignmentMapFiltersProps {
  filters: AlignmentFilters;
  onFiltersChange: (filters: AlignmentFilters) => void;
  isOpen: boolean;
  onClose: () => void;
  totalOKRs: number;
  visibleOKRs: number;
  availableDepartments: string[];
}

export const AlignmentMapFilters: React.FC<AlignmentMapFiltersProps> = ({
  filters,
  onFiltersChange,
  isOpen,
  onClose,
  totalOKRs,
  visibleOKRs,
  availableDepartments,
}) => {
  // Toggle type filter
  const toggleType = (type: string) => {
    const newTypes = new Set(filters.types);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    onFiltersChange({ ...filters, types: newTypes });
  };

  // Toggle status filter
  const toggleStatus = (status: string) => {
    const newStatuses = new Set(filters.statuses);
    if (newStatuses.has(status)) {
      newStatuses.delete(status);
    } else {
      newStatuses.add(status);
    }
    onFiltersChange({ ...filters, statuses: newStatuses });
  };

  // Toggle department filter
  const toggleDepartment = (dept: string) => {
    const newDepartments = new Set(filters.departments);
    if (newDepartments.has(dept)) {
      newDepartments.delete(dept);
    } else {
      newDepartments.add(dept);
    }
    onFiltersChange({ ...filters, departments: newDepartments });
  };

  // Handle time period presets
  const setTimePeriodPreset = (preset: string) => {
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (preset === 'current-quarter') {
      const { year, quarter } = getCurrentQuarter();
      const dates = getQuarterDates(year, quarter);
      startDate = dates.start;
      endDate = dates.end;
    } else if (preset === 'next-quarter') {
      const { year, quarter } = getNextQuarter();
      const dates = getQuarterDates(year, quarter);
      startDate = dates.start;
      endDate = dates.end;
    } else if (preset === 'current-year') {
      const dates = getYearDates(new Date().getFullYear());
      startDate = dates.start;
      endDate = dates.end;
    }

    onFiltersChange({
      ...filters,
      timePeriod: { startDate, endDate, preset },
    });
  };

  // Handle custom date change
  const setCustomDateRange = (startDate: Date | null, endDate: Date | null) => {
    onFiltersChange({
      ...filters,
      timePeriod: { startDate, endDate, preset: 'custom' },
    });
  };

  // Clear all filters
  const clearFilters = () => {
    onFiltersChange({
      types: new Set(['company', 'department', 'team', 'individual']),
      statuses: new Set(),
      search: '',
      progressMin: 0,
      progressMax: 100,
      departments: new Set(),
      timePeriod: { startDate: null, endDate: null, preset: 'all' },
    });
  };

  // Count active filters
  const activeFilterCount =
    (4 - filters.types.size) + // Types not selected
    filters.statuses.size +
    (filters.search ? 1 : 0) +
    ((filters.progressMin > 0 || filters.progressMax < 100) ? 1 : 0) +
    filters.departments.size +
    (filters.timePeriod.preset !== 'all' ? 1 : 0);

  const typeOptions = [
    { value: 'company', label: 'Company', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { value: 'department', label: 'Department', color: 'bg-green-100 text-green-800 border-green-300' },
    { value: 'team', label: 'Team', color: 'bg-green-100 text-green-800 border-green-300' },
    { value: 'individual', label: 'Individual', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', variant: 'success' as const },
    { value: 'on_track', label: 'On Track', variant: 'success' as const },
    { value: 'at_risk', label: 'At Risk', variant: 'warning' as const },
    { value: 'off_track', label: 'Off Track', variant: 'danger' as const },
    { value: 'completed', label: 'Completed', variant: 'success' as const },
    { value: 'draft', label: 'Draft', variant: 'secondary' as const },
    // S1-g. This list is hardcoded rather than derived from the data, so closed OKRs — the
    // majority — could not be filtered for or against on the alignment map at all.
    { value: 'closed', label: 'Closed', variant: 'secondary' as const },
  ];

  if (!isOpen) return null;

  return (
    <div className="absolute left-0 top-0 bottom-0 w-80 bg-white border-r border-stone-200 shadow-lg z-40 overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-white" />
          <h2 className="font-semibold text-white">Filters</h2>
          {activeFilterCount > 0 && (
            <Badge variant="primary" size="sm" className="bg-white text-primary-600">
              {activeFilterCount}
            </Badge>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          aria-label="Close filters"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Results Count */}
      <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
        <p className="text-sm text-stone-600">
          Showing <span className="font-semibold text-stone-900">{visibleOKRs}</span> of{' '}
          <span className="font-semibold text-stone-900">{totalOKRs}</span> OKRs
        </p>
      </div>

      {/* Filters */}
      <div className="p-4 space-y-6">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
              placeholder="Search OKR titles..."
              className="w-full pl-10 pr-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* OKR Types */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            OKR Type
          </label>
          <div className="space-y-2">
            {typeOptions.map((type) => (
              <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.types.has(type.value)}
                  onChange={() => toggleType(type.value)}
                  className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-primary-500"
                />
                <span className={`text-sm px-2 py-0.5 rounded border ${type.color}`}>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Status
          </label>
          <div className="space-y-2">
            {statusOptions.map((status) => (
              <label key={status.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.statuses.has(status.value)}
                  onChange={() => toggleStatus(status.value)}
                  className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-primary-500"
                />
                <Badge variant={status.variant} size="sm">
                  {status.label}
                </Badge>
              </label>
            ))}
          </div>
        </div>

        {/* Progress Range */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2">
            Progress Range
          </label>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                max="100"
                value={filters.progressMin}
                onChange={(e) => onFiltersChange({
                  ...filters,
                  progressMin: Math.max(0, Math.min(100, parseInt(e.target.value) || 0))
                })}
                className="w-20 px-2 py-1 border border-stone-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-stone-500">to</span>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.progressMax}
                onChange={(e) => onFiltersChange({
                  ...filters,
                  progressMax: Math.max(0, Math.min(100, parseInt(e.target.value) || 100))
                })}
                className="w-20 px-2 py-1 border border-stone-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-stone-500">%</span>
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div className="text-xs text-stone-600">{filters.progressMin}%</div>
                <div className="text-xs text-stone-600">{filters.progressMax}%</div>
              </div>
              <div className="relative h-2 bg-stone-200 rounded-full">
                <div
                  className="absolute h-full bg-primary-500 rounded-full"
                  style={{
                    left: `${filters.progressMin}%`,
                    right: `${100 - filters.progressMax}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Departments */}
        {availableDepartments.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Department
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {availableDepartments.map((dept) => (
                <label key={dept} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.departments.has(dept)}
                    onChange={() => toggleDepartment(dept)}
                    className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-stone-700">{dept}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Time Period */}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Time Period
          </label>

          {/* Quick Presets */}
          <div className="space-y-2 mb-3">
            <Button
              variant={filters.timePeriod.preset === 'all' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setTimePeriodPreset('all')}
              className="w-full justify-start text-sm"
            >
              All Time
            </Button>
            <Button
              variant={filters.timePeriod.preset === 'current-quarter' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setTimePeriodPreset('current-quarter')}
              className="w-full justify-start text-sm"
            >
              Current Quarter (Q{getCurrentQuarter().quarter} {getCurrentQuarter().year})
            </Button>
            <Button
              variant={filters.timePeriod.preset === 'next-quarter' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setTimePeriodPreset('next-quarter')}
              className="w-full justify-start text-sm"
            >
              Next Quarter (Q{getNextQuarter().quarter} {getNextQuarter().year})
            </Button>
            <Button
              variant={filters.timePeriod.preset === 'current-year' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setTimePeriodPreset('current-year')}
              className="w-full justify-start text-sm"
            >
              Current Year ({new Date().getFullYear()})
            </Button>
          </div>

          {/* Custom Date Range */}
          <div className="pt-3 border-t border-stone-200">
            <p className="text-xs font-medium text-stone-700 mb-2">Custom Range</p>
            <div className="space-y-2">
              <div>
                <label className="text-xs text-stone-600 mb-1 block">Start Date</label>
                <input
                  type="date"
                  value={filters.timePeriod.startDate ? filters.timePeriod.startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const newStart = e.target.value ? new Date(e.target.value) : null;
                    setCustomDateRange(newStart, filters.timePeriod.endDate);
                  }}
                  className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="text-xs text-stone-600 mb-1 block">End Date</label>
                <input
                  type="date"
                  value={filters.timePeriod.endDate ? filters.timePeriod.endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const newEnd = e.target.value ? new Date(e.target.value) : null;
                    setCustomDateRange(filters.timePeriod.startDate, newEnd);
                  }}
                  className="w-full px-2 py-1.5 border border-stone-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Clear Filters */}
      <div className="sticky bottom-0 bg-stone-50 border-t border-stone-200 px-4 py-3">
        <Button
          variant="outline"
          onClick={clearFilters}
          disabled={activeFilterCount === 0}
          className="w-full"
        >
          Clear All Filters
        </Button>
      </div>
    </div>
  );
};
